/**
 * metricInfo — what every graph on the Classic dashboard actually counts.
 *
 * The brief: "add an 'i' information icon to every graphical report explaining
 * which parameters/data points are being considered." This file is that
 * content, in one place, so the answer cannot drift panel by panel.
 *
 * THE TWO RULES THIS COPY OBEYS (see CLASSIC_DASHBOARD_HANDOFF.md):
 *
 *   1. The source split is INTERNAL. Nothing here may name a collector —
 *      no "app", "agent", "stealth", "desktop". Only coverage surfaces, and
 *      only as "based on N of M people".
 *   2. Raw capture is never a product surface. Nothing here may say screen,
 *      screenshot, OCR, or capture. We describe PARAMETERS — active time,
 *      idle time, hours, presence — never the mechanism that observed them.
 *
 * Shape:
 *   key   → the panel id an InfoDot is given
 *   h     → the panel's own name
 *   k     → the eyebrow ("What this counts" / "What the stack counts" …)
 *   p     → [parameter, what it means] pairs, in the order a reader needs them
 *   n     → optional note for the one thing a reader would otherwise get wrong
 *
 * Anything user-visible about a number belongs here rather than in a tooltip
 * string next to the chart, so that a copy change lands everywhere at once.
 */

export const METRIC_INFO = {
  /* ── KPI strip ──────────────────────────────────────────────────────── */
  hours: {
    h: "Hours tracked",
    k: "What this counts",
    p: [
      ["Tracked time", "Every minute a member's device reported work in the selected range."],
      ["Day boundary", "Days are cut on the organisation clock, not the viewer's."],
      ["Deduplication", "A minute is counted once per person per day, even when two channels report it."],
      ["Excluded", "Manually discarded time, and time outside the range."],
    ],
    n: "The delta compares the same number of calendar days immediately before this range.",
  },
  active: {
    h: "Active time",
    k: "What this counts",
    p: [
      ["Active", "Tracked minutes with keyboard or pointer input inside the sampling window."],
      ["Not active", "Windows with no input, and any time the device was locked or asleep."],
      ["Percentage", "Active ÷ tracked. Untracked time is excluded from both sides."],
    ],
    n: "Active is a presence measure, not a productivity verdict. TeamGrid does not rate applications as productive or unproductive.",
  },
  idle: {
    h: "Idle time",
    k: "What this counts",
    p: [
      ["Idle", "Tracked minutes inside a running session with no input for the idle threshold."],
      ["Threshold", "The organisation's idle setting — 5 minutes by default."],
      ["Not idle", "Time outside any session. That is untracked, and is counted separately."],
    ],
    n: "Long idle inside a session often means a meeting or a call away from the keyboard.",
  },
  presence: {
    h: "People tracking",
    k: "What this counts",
    p: [
      ["Tracked", "Members with at least one minute of activity in the range."],
      ["Silent", "Members with an active account and zero minutes."],
      ["Excluded", "Deactivated accounts, and members outside your access scope."],
    ],
    n: "Silent means nothing arrived. It never implies the person did not work.",
  },
  projects: {
    h: "Active projects",
    k: "What this counts",
    p: [
      ["Active project", "A project with at least one tracked minute in the range."],
      ["Attribution", "Hours attach to the project selected on the member's device at the time."],
      ["No project", "Hours with no selection are grouped as Unassigned, never spread across the others."],
    ],
  },
  goal: {
    h: "Goal score",
    k: "What this counts",
    p: [
      ["Score", "Weighted completion across every goal active in the range."],
      ["Weighting", "Each goal's configured weight; unweighted goals count equally."],
      ["On track", "Progress at or above the pace the goal's own deadline implies."],
      ["Excluded", "Draft goals, and goals whose window has not opened."],
    ],
  },

  /* ── charts ─────────────────────────────────────────────────────────── */
  rhythm: {
    h: "Hours per day",
    k: "What the stack counts",
    p: [
      ["Active", "Tracked minutes with input."],
      ["Idle", "Tracked minutes without input, inside a session."],
      ["Untracked", "Scheduled working time with nothing reported. Shown as the top band, and NOT counted in the figure under each bar."],
      ["Dashed line", "The same weekday in the previous period, same scope."],
      ["Bucketing", "Each minute lands on its organisation-clock calendar day."],
    ],
    n: "Bar height is total tracked time for the whole selection, not per person.",
  },
  matrix: {
    h: "When the team is on",
    k: "What each spoke counts",
    p: [
      ["Spoke", "One hour of the organisation clock."],
      ["Length", "Average number of people active in that hour across the range."],
      ["Peak", "The longest spoke — the hour most of the team was on."],
      ["Clock", "Hours are organisation-local."],
    ],
    n: "Hover a spoke for the head-count in that hour.",
  },
  curve: {
    h: "Working-hour curve",
    k: "What this counts",
    p: [
      ["Height", "Average number of people active in that hour across the range."],
      ["Average", "Divided by days that had any activity, so weekends do not flatten it."],
      ["Band", "First to last activity across the whole selection."],
    ],
  },
  category: {
    h: "Category allocation",
    k: "What this counts",
    p: [
      ["Slice", "Share of total tool time in that category."],
      ["Category", "Assigned per application or site by the organisation's category map."],
      ["Uncategorised", "Grouped as Other rather than dropped."],
      ["Base", "Tool time only — idle and untracked time are not in this chart."],
    ],
  },
  tools: {
    h: "Tools in use",
    k: "What this counts",
    p: [
      ["Share", "Share of total tool time for that application or site."],
      ["People", "How many distinct people used it at all in the range."],
      ["Sites", "Folded to the domain. Full addresses are never shown."],
      ["Order", "By share of time, not by head-count."],
    ],
  },
  projectShare: {
    h: "Project share",
    k: "What this counts",
    p: [
      ["Slice", "That project's tracked minutes ÷ all tracked minutes."],
      ["Members", "Distinct people with time on the project in the range."],
      ["Delta", "Against the previous period of equal length."],
      ["Unassigned", "Its own slice. Never redistributed across named projects."],
    ],
    n: "Project attribution comes from the selection made on the device, so hours recorded without a selection stay unassigned.",
  },
  people: {
    h: "Team Activity Log",
    k: "What each row counts",
    p: [
      ["Bar length", "That person's tracked time in the range."],
      ["Blue", "Active minutes — input was seen."],
      ["Amber", "Idle minutes — inside a session, no input."],
      ["Silent", "Nothing arrived for them in this window."],
    ],
  },
  alerts: {
    h: "Today's story",
    k: "How this list is written",
    p: [
      ["Source", "Named work from the written record for this window."],
      ["Writer", "Haiku. At most three writes per organisation day — morning, midday, evening."],
      ["Story", "One person and one named piece of work. Nothing invented."],
      ["Door", "Open a story to read it, then open that person."],
    ],
    n: "This is a news desk, not a blocker list. Quiet means nothing named landed yet.",
  },
  attention: {
    h: "How this list is built",
    k: "Ranking",
    p: [
      ["Rank", "By how many hours each observation accounts for — largest first."],
      ["Silence", "Any member with an active account and zero minutes."],
      ["Idle spike", "A day whose idle share sits more than 3 points above the period average."],
      ["Project drop", "A project down more than 10% against the previous period."],
    ],
    n: "Every line is computed from stored figures. Nothing on this list is written by a model.",
  },
  flow: {
    h: "Where attention moves",
    k: "What this counts",
    p: [
      ["Path", "One recorded move from one application to another."],
      ["Thickness", "How many times that move happened in the range."],
      ["Shown", "The heaviest paths. The long tail is folded away."],
      ["Coverage", "Only part of the fleet reports activity detail — the count under the chart says how much."],
    ],
    n: "The panel does not render at all when no one in the current selection produces activity detail, rather than showing an empty frame.",
  },
  focus: {
    h: "Focus",
    k: "What this counts",
    p: [
      ["Switches", "Recorded moves between applications in the range."],
      ["Stretch", "An unbroken run inside one application."],
      ["Coverage", "Based on activity detail from part of the fleet — the count beneath says how much."],
    ],
    n: "Fewer switches is not automatically better. Some roles are meant to move between tools.",
  },

  /* ── Pulse-only panels ──────────────────────────────────────────────── */
  orgHealth: {
    h: "Org health",
    k: "How the score is built",
    p: [
      ["Engagement", "How much of the team reported activity at all, against the size of the team."],
      ["Focus", "Active share of tracked time — the same measure the Active tile uses."],
      ["Balance", "How evenly tracked hours are spread across the people who reported."],
      ["Delivery", "Weighted goal completion for goals active in the range."],
      ["Score", "The four bands above, averaged. Bands with no data are left out rather than counted as zero."],
    ],
    n: "A composite is a starting point, not a verdict. Each band is clickable — read the band before acting on the number.",
  },
  spend: {
    h: "Where the organisation spent itself",
    k: "What the areas count",
    p: [
      ["Area", "That project's share of tracked time. Area is the measure, not width or colour."],
      ["Movement", "Hours against the previous period of equal length."],
      ["Unassigned", "Hours recorded without a project selection stay their own block, never spread across the named projects."],
      ["Base", "Tracked time only — untracked scheduled time is not in this chart."],
    ],
  },
  dayShape: {
    h: "The shape of a working day",
    k: "What this counts",
    p: [
      ["Curve", "Share of the team active in each hour, averaged across the days in range."],
      ["Clock", "Hours are organisation-local, not the viewer's."],
      ["Average", "Divided by days that had any activity, so quiet days do not flatten it."],
    ],
  },
};

/** Every panel id that has ⓘ copy — used by the coverage check in the tests. */
export const INFO_KEYS = Object.keys(METRIC_INFO);

export default METRIC_INFO;
